import type { Config } from "../../../../aysa_api/models/config.types";
import type {
  Session,
  AnySession,
  OptionalCustomerSession,
} from "../../../../aysa_api/models/session.types";
import type { TargetGroup } from "../../../../aysa_api/models/target_group.types";
import type { ReductionsState } from "../../../../aysa_api/models/reductions_state.types";
import type { InStreamSimilarProductsSection } from "../../../../aysa_api/models/sections.types";
import type {
  ProductImageTypes,
  ProductImage,
} from "../../../../aysa_api/models/product_images.types";
import type { BasketToken } from "../../../../aysa_api/models/basket_token.types";
import type { ProductDetailsSection } from "../../../../aysa_api/services/article_detail_page/v1/product_details.types";
import type { LinksSection } from "../../../../aysa_api/models/links_section.types";
import type { TrackingSection } from "../../../../aysa_api/services/article_detail_page/v1/tracking_section.types";
import type {
  FooterSection,
  FooterDynamic,
} from "../../../../aysa_api/models/footer.types";
import type { CoinsFeatureInfo } from "../../../../aysa_api/models/coins_feature_info.types";
import type { Link } from "../../../../aysa_api/models/link.types";
import type { RichText } from "../../../../aysa_api/models/rich_text.types";
import type { Product } from "../../../../aysa_api/models/product.types";
import type { Coupon } from "../../../../aysa_api/models/coupon.types";
import type { Color } from "../../../../aysa_api/models/color.types";
import type {
  SectionTracker,
  FeatureTracker,
  ProductTracker,
  BrandTracker,
  PromotionTracker,
} from "../../../../aysa_api/models/tracker.types";
import type {
  Badge,
  QualitativeBadge,
} from "../../../../aysa_api/models/badge.types";
import type { LinkTarget } from "../../../../aysa_api/models/link_target.types";
import type { Brand } from "../../../../aysa_api/models/brand.types";
import type { Gallery } from "../../../../aysa_api/models/gallery.types";
import type { Image } from "../../../../aysa_api/models/image.types";
import type { Price } from "../../../../aysa_api/models/price.types";
import type { PriceV2 } from "../../../../aysa_api/models/price_v2.types";
import type { BrandPreview } from "../../../../aysa_api/models/brand_preview.types";
import type { SizeSelection } from "../../../../aysa_api/models/size_selection.types";
import type { Advertisement } from "../../../../aysa_api/models/advertisement.types";
import type { USP } from "../../../../aysa_api/models/usp.types";
import type { ProgressUntilFreeDeliverySection } from "../../../../aysa_api/models/basket_mov.types";
import type { MissingAttributesHint } from "../../../../aysa_api/models/size_finder.types";
import type {
  StarRating,
  ProductReview,
  StarRatingDistribution,
} from "../../../../aysa_api/models/product_reviews.types";
import type {
  Recommendation,
  OutfitSlider,
  ProductRecommendation_BrandContent,
  ProductRecommendation_TeaserContent,
} from "../../../../aysa_api/services/article_detail_page/v1/recommendation.types";
import type { Channel } from "../../../../aysa_api/models/channel.types";
import type { ProductTile } from "../../../../aysa_api/models/product_tile.types";
import type { ProductTilePreferences } from "../../../../aysa_api/models/product_tile_preferences.types";
import type { ProductTileV2 } from "../../../../aysa_api/models/product_tile_v2.types";
import type { ProductSelection } from "../../../../aysa_api/models/product_selection.types";

// Type of recommendation in AI dialogue / GetSpecificRecommendation.
export enum RecoType {
  UNSPECIFIED = 0,
  BRAND = 1,
  SIMILAR = 2,
  STORY = 3,
  COMPLEMENTARY = 4,
}

export enum ReviewsSection_ReviewSummary_ActionStatement_ActionStatementType {
  UNSPECIFIED = 0,
  FIT = 1,
  QUALITY = 2,
}

export enum ReportLegalConcernFormRequest_Reason {
  UNSPECIFIED = 0,
  INFRINGEMENT_OF_IP = 1,
  INCORRECT_INFO = 2,
  INAPPROPRIATE_CONTENT = 3,
  NON_COMPLIANT = 4,
  OTHER_LEGAL_OFFENSE = 5,
  PRODUCT_SAFETY = 6,
}

export interface InStreamSimilarProductsRequest {
  config: Config;
  session: Session;
  // / only passed from brand page
  brandId?: number;
  productId: number;
  // [-5, +5] products around the [product_id] in the stream
  // avoids repeating products in the recos
  excludedProductIds: number[];
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
}

export interface InStreamSimilarProductsResponse {
  // null when no recos
  instreamSimilarProducts?: InStreamSimilarProductsSection;
}

export interface GetProductBulkRequest {
  config: Config;
  session: AnySession;
  productId: number;
  // The currently selected target group by the client.
  //
  // This is currently used for determining the correct profile
  // the Size Finder integration when the `enable_size_finder` flag is set to `true`.
  targetGroup: TargetGroup;
  // Whether or not the server should enable the Size Finder integration.
  //
  // This affects the badges, the `SizeFinderHintSection` and the `SizesSection`.
  enableSizeFinder: boolean;
  // The preferred product image which should be shown first in the images section.
  //
  // In case the field is unspecified, we will fallback to showing bust images first.
  preferredProductImage: ProductImageTypes;
  reductionsState: ReductionsState;
  selectedCouponPromotionId?: string;
  basketToken?: BasketToken;
  // Whether or not to enable the Fitmatch integration.
  //
  // This affects the Badges and the Size Selection.
  enableFitmatch: boolean;
  // This flag controls if we need to display fitmatch scan entrypoints
  fitmatchScanningCapableDevice: boolean;
  // Whether or not to show the generated images from AIUTA in the image gallery.
  // Based on the consent the customer has given during the Try-on flow.
  showAiutaGeneratedImages: boolean;
  // Whether or not to enable the AIUTA integration.
  //
  // This might affect the Badges.
  enableAiuta: boolean;
  // we only want to show the dynamic footer content on direct entries to the ADP and dont want to show it when navigating other pages to ADP
  includeFooterDynamic: boolean;
  // Deprecated: use [with_basic_product_info_only] in GetProductDetailsBulkRequest instead
  withBasicProductInfoOnly: boolean;
}

export interface ProductBulkError {
  type?: {
    $case: "couponFail";
    couponFail: ProductBulkError_CouponFail;
  };
}

export interface ProductBulkError_CouponFail {
  // E.g.: "Sorry! We're having difficulties loading coupon prices right now..."
  message: string;
}

export interface GetProductBulkResponse {
  imagesSection: ImagesSection;
  priceAndBrandSection: PriceAndBrandSection;
  productSelectionSection: ProductSelectionSection;
  sizesSection: SizesSection;
  productDetailsSection: ProductDetailsSection;
  merchantBannerSection: MerchantBannerSection;
  uspsSection: USPsSection;
  reviewSection: ReviewsSection;
  sizeFinderHintSection: SizeFinderHintSection;
  linksSection: LinksSection;
  trackingSection: TrackingSection;
  basPlacementSection: BASPlacementSection;
  hotProductSection: HotProductSection;
  footerSection: FooterSection;
  prominentBannerSection?: ProminentBannerSection;
  basketToken?: BasketToken;
  error?: ProductBulkError;
  // only sent for the romanian shop at the moment
  bestPriceHintSection?: PriceGuaranteeHintSection;
  aiutaSection?: AiutaSection;
  legalReportSection: LegalReportSection;
  perfectPairRecoSection?: PerfectPairRecoSection;
  coinsBannerSection?: CoinsBannerSection;
  // App only
  productCategories?: ProductCategories;
  // this section will be returned only the include_footer_dynamic flag is set to true
  // this is Web only as of now
  dynamicFooterSection?: FooterDynamic;
}

// https://aboutyou.atlassian.net/browse/ASBE-4874 - enabling app to determine if they want to inject the last seen items in the category page
export interface ProductCategories {
  productId: number;
  // The categories of the product
  categories: ProductCategories_Category[];
}

export interface ProductCategories_Category {
  categoryId: number;
  categoryDepth: number;
}

export interface CoinsBannerSection {
  coinsToBeEarned: number;
  coinsInfoSheet: CoinsBannerSection_CoinsInfoSheet;
}

export interface CoinsBannerSection_CoinsInfoSheet {
  // eg. Coins
  title: string;
  // eg. Continue shopping
  ctaText: string;
  // eg. How does it work?
  headline: string;
  // eg. The final amount of Coins you will earn for an order will be determined after Checkout.
  infoText: string;
  featureInfos: CoinsFeatureInfo[];
}

export interface LegalReportSection {
  // e.g. "Report a legal concern"
  cta: string;
  popup: LegalReportSection_Popup;
}

export interface LegalReportSection_Popup {
  // e.g. "Report a legal concern"
  title: string;
  // e.g. "Please use this form if you wish to report illegal content or if you have product safety concerns in accordance with EU regulations."
  introText: string;
  // use additional_text instead this field is deprecated because it can't handle bold text
  deprecatedAdditionalText: string;
  // e.g. "Help Center"
  helpCenterLink: Link;
  // e.g. "Cancel"
  cancelLabel: string;
  // e.g. label "Report a concern", link "/h/legal-report/:productId"
  confirmCta: Link;
  // e.g <b>Please note:</b> Order inquiries won't be processed through this form. Please contact customer service or check our FAQ for support.
  additionalText: RichText;
}

export interface AiutaSection {
  // eg 'Upload your photo and try it on!'
  text: string;
  // eg 'Try on'
  cta: string;
}

export interface ProminentBannerSection {
  product: Product;
  error?: ProminentBannerSection_ProminentBannerError;
  type?:
    | {
        $case: "lowStock";
        lowStock: ProminentBannerSection_LowStock;
      }
    | {
        $case: "campaignCountdown";
        campaignCountdown: ProminentBannerSection_CampaignCountdown;
      }
    | {
        $case: "couponSlider";
        couponSlider: ProminentBannerSection_CouponSlider;
      };
}

export interface ProminentBannerSection_CampaignCountdown {
  backgroundColor: string;
  textColor: string;
  endsAt: Date;
}

export interface ProminentBannerSection_LowStock {
  // eg Only x items left
  label: string;
}

export interface ProminentBannerSection_CouponSlider {
  // eg: Save up to -25% 🎉
  headline: string;
  coupons: Coupon[];
  backgroundColor: Color;
  foregroundColor: Color;
  // if a selected_coupon_promotion_id is passed in the request, then the applied_coupon_promotion_id will be returned
  // indicating if the coupon was successfully activated or not
  // if no selected_coupon_promotion_id is passed in the request, then the applied_coupon_promotion_id will not be returned
  appliedCouponPromotionId?: string;
}

export interface ProminentBannerSection_ProminentBannerError {
  type?: {
    $case: "couponFail";
    couponFail: ProminentBannerSection_ProminentBannerError_CouponFail;
  };
}

export interface ProminentBannerSection_ProminentBannerError_CouponFail {
  // E.g.: "Sorry! We're having difficulties loading coupon prices right now..."
  message: string;
}

export interface PriceGuaranteeHintSection {
  // headline for the section eg. "Spotted a better deal? Claim our Best Price Guarantee”
  headline: string;
  flyout: PriceGuaranteeHintSection_Flyout;
  tracker: SectionTracker;
}

export interface PriceGuaranteeHintSection_Flyout {
  // headline for the flyout eg. "Best price guarantee"
  headline: string;
  body: PriceGuaranteeHintSection_Flyout_Body;
  cta: Link;
}

export interface PriceGuaranteeHintSection_Flyout_Body {
  // Terms and Conditons body text
  tnc: string;
}

export interface ImagesSection {
  images: ImagesSection_Image[];
  // A list of badges which should be shown above the images
  badges: Badge[];
  // The link to go back to from the ADP on direct entry
  // Should be the product category with product brand as preset filter
  // In case product has no category, no link is returned and no back button shown
  deprecatedBackLinkTarget?: LinkTarget;
  product: Product;
  brand: Brand;
  videoButton?: ImagesSection_VideoButton;
  // A list of qualitative badges
  qualitativeBadges: QualitativeBadge[];
  gallery: Gallery;
}

export interface ImagesSection_Image {
  // Label for Shop The Look text
  // If the label is set, then the image is Shop The Look image
  shopTheLookLabel?: string;
  image: ProductImage;
}

export interface ImagesSection_VideoButton {
  videoUrl: string;
  // Translated button label eg. Video
  label: string;
  icon: Image;
  // Translated button description eg. Product Video Preview
  description: string;
}

export interface PriceAndBrandSection {
  product: Product;
  brandLogo: PriceAndBrandSection_BrandLogoSection;
  // Sale or Campaign Sale badge to show in Product information section
  // Deprecated
  // - use [price] instead for badges
  // - use ProminentBannerSection for CampaignCountdown
  saleBadges: Badge[];
  // Not sent if sold out
  // will include price.reduction.badge & price.lpl30
  // use [price_v2] instead
  price?: Price;
  // Extended product name which can be used for SEO
  // In format of: {brand} {product_name} in {color}
  // Same content as the one in ProductDetailsSection
  seoProductName?: string;
  // Not sent if sold out
  // will include price.badge & price.lpl30
  priceV2?: PriceV2;
  // Used on Web stacks to update the price on the ADP itself, when a new size is selected
  // Key is size_id
  sizePricesV2: { [key: number]: PriceV2 };
}

export interface PriceAndBrandSection_BrandLogoSection {
  brand: Brand;
  // Path to second hand category filtered by brand if product is second hand. Otherwise path to brand page.
  path: string;
  // This will mainly be used for preloading the logo image when navigating from ADP to brand page
  brandPreview?: BrandPreview;
}

export interface PriceAndBrandSection_SizePricesV2Entry {
  key: number;
  value: PriceV2;
}

export interface ProductSelectionSection {
  product: Product;
  type?:
    | {
        $case: "productSelection";
        productSelection: ProductSelection;
      }
    | {
        $case: "noSiblings";
        noSiblings: ProductSelectionSection_NoSiblings;
      };
}

export interface ProductSelectionSection_NoSiblings {
  // The label of the product the user is currently on
  colorLabel: string;
}

export interface SizesSection {
  sizeSelection: SizeSelection;
  // Brand is required for brand logo in the size reminder
  brand: Brand;
  // Product information like image and name required for size reminder
  product: Product;
  // Sale or Campaign Sale badge to show in Size Flyout Header
  // Deprecated: use [price] instead
  saleBadges: Badge[];
  // use [price_v2] instead
  price?: Price;
  // If the product is to be matched using Fitmatch SDK
  isFitmatchEligible: boolean;
  priceV2?: PriceV2;
}

export interface MerchantBannerSection {
  // Available only for 2nd hand prodcuts which have `attribute:merchantName` set
  merchantBanner?: Advertisement;
}

export interface USPsSection {
  // Behavior:
  // * Product is second hand: include second hand USPs + normal USP 1 + normal USP 2 only, in that order.
  // * Product is not second hand: include normal USP 1 + USP 2 + USP 3 only, in that order.
  // * User is VIP: include VIP USP 1...6 only.
  // * User is VIP + product is second hand: exactly like above (do not include second hand USPs).
  usps: USP[];
  articleUsps: USPsSection_ArticleUSP[];
}

export interface USPsSection_ArticleUSP {
  type?:
    | {
        $case: "standard";
        standard: USPsSection_ArticleUSP_Standard;
      }
    | {
        $case: "movSection";
        movSection: USPsSection_ArticleUSP_MOV;
      }
    | {
        $case: "deliveryEstimationSection";
        deliveryEstimationSection: USPsSection_ArticleUSP_DeliveryEstimation;
      };
}

export interface USPsSection_ArticleUSP_Standard {
  label: string;
  image: Image;
  isVip: boolean;
  isSecondHand: boolean;
  targetUrl?: LinkTarget;
}

export interface USPsSection_ArticleUSP_DeliveryEstimation {
  // e.g. From 1-2 working days
  label: string;
  image: Image;
  deliveryTimeHint?: USPsSection_ArticleUSP_DeliveryEstimation_DeliveryTimeHint;
  // e.g. Select a size for more precise delivery information
  description: string;
  infoBox: USPsSection_ArticleUSP_DeliveryEstimation_InfoBox;
  // e.g. if no size is selected, this is the usp information that is displayed
  sizeId?: number;
  featureTracker: FeatureTracker;
}

export interface USPsSection_ArticleUSP_DeliveryEstimation_DeliveryTimeHint {
  // e.g. Fast Delivery
  label: string;
  // e.g. #FF0000
  color: Color;
  vipImage?: Image;
}

export interface USPsSection_ArticleUSP_DeliveryEstimation_InfoBox {
  // e.g. Final expected delivery times
  header: string;
  // e.g. Depending on the composition of your basket, delivery times can change between the display here...
  text: string;
}

export interface USPsSection_ArticleUSP_MOV {
  image: Image;
  freeDeliveryProgressSection: ProgressUntilFreeDeliverySection;
  targetUrl?: LinkTarget;
}

export interface SizeFinderHintSection {
  sizeFinderHint?: MissingAttributesHint;
}

export interface ReviewsSection {
  summary?: ReviewsSection_ReviewSummary;
}

export interface ReviewsSection_ReviewSummary {
  // Number of stars to be displayed
  // Can only be: 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5 or 5
  overallStarRating: StarRating;
  // Average rating between 1 and 5 with 1 decimal (for example: 3.4)
  overallRating: number;
  // Total number of reviews posted for the given product
  reviewCount: number;
  actionStatements: ReviewsSection_ReviewSummary_ActionStatement[];
  // A curated group of product reviews that represent the overall feedback for the given product
  showcaseReviews: ProductReview[];
  deprecatedRatingDistribution: StarRatingDistribution[];
  // Distribution of ratings in percent, sorted in descending order: from (best) 5 stars to (worst) 1 star
  ratingDistribution: StarRatingDistribution[];
}

// Secondary ratings such as FIT and QUALITY are returned as ActionStatement
export interface ReviewsSection_ReviewSummary_ActionStatement {
  headline: string;
  subline: string;
  type: ReviewsSection_ReviewSummary_ActionStatement_ActionStatementType;
}

export interface ProductPerformanceInfoBox {
  // Headline without the emoji. Example: "Hot Product"
  headline: string;
  subline: string;
}

export interface HotProductSection {
  infoBox?: ProductPerformanceInfoBox;
}

export interface GetRecommendationsRequest {
  config: Config;
  session: OptionalCustomerSession;
  productId: number;
  lastSeenProductIds: number[];
  // The currently selected target group by the client.
  //
  // If a given product's target group is unspecified, then the client target group will be used.
  // In case client target group is also unspecified, then it defaults to the female target group.
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
}

export interface GetRecommendationsResponse {
  recommendation: Recommendation;
}

export interface SubscribeToSizeReminderRequest {
  config: Config;
  session: OptionalCustomerSession;
  sizeIds: number[];
  deprecatedEmail: string;
  channels: Channel[];
}

// If a native gRPC error is returned, then the endpoint failed
// otherwise subscription was successful
export interface SubscribeToSizeReminderResponse {}

export interface GetProductDetailsBulkRequest {
  config: Config;
  session: OptionalCustomerSession;
  // When true, the server may omit non-essential sections and return only basic product information.
  withBasicProductInfoOnly: boolean;
  productId: number;
  reductionsState: ReductionsState;
}

export interface GetProductDetailsBulkResponse {
  // use shop the look provided in following response:
  // aysa_api.services.article_detail_page.v1.ProductRecommendation
  shopTheLookSection: ShopTheLookSection;
  productDetailsSection: ProductDetailsSection;
  reviewSection: ReviewsSection;
}

export interface MiniADP {
  imagesSection: ImagesSection;
  priceAndBrandSection: PriceAndBrandSection;
  productSelectionSection: ProductSelectionSection;
  sizesSection: SizesSection;
  prominentBannerSection?: ProminentBannerSection;
}

export interface ShopTheLookSection {
  shopTheLook?: ShopTheLookSection_ShopTheLook;
}

export interface ShopTheLookSection_ProductTiles {
  deprecatedProductTiles: ProductTile[];
  itemsPreferences: ProductTilePreferences;
  productTiles: ProductTileV2[];
}

export interface ShopTheLookSection_ShopTheLook {
  shopTheLook?:
    | {
        $case: "singleProduct";
        singleProduct: MiniADP;
      }
    | {
        $case: "productTiles";
        productTiles: ShopTheLookSection_ProductTiles;
      };
}

export interface GetMinimalProductBulkRequest {
  config: Config;
  session: AnySession;
  productId: number;
  // The currently selected target group by the client.
  //
  // This is currently used for determining the correct profile
  // the Size Finder integration when the `enable_size_finder` flag is set to `true`.
  targetGroup: TargetGroup;
  // Whether or not the server should enable the Size Finder integration.
  //
  // This affects the badges, the `SizeFinderHintSection` and the `SizesSection`.
  enableSizeFinder: boolean;
  reductionsState: ReductionsState;
  selectedCouponPromotionId?: string;
  basketToken?: BasketToken;
  preferredProductImageType?: ProductImageTypes;
  // Whether or not to enable the Fitmatch integration.
  //
  // This affects the Badges and the Size Selection.
  enableFitmatch: boolean;
  // This flag controls if we need to display fitmatch scan entrypoints
  fitmatchScanningCapableDevice: boolean;
}

export interface GetMinimalProductBulkResponse {
  imagesSection: ImagesSection;
  priceAndBrandSection: PriceAndBrandSection;
  productSelectionSection: ProductSelectionSection;
  sizesSection: SizesSection;
  linksSection: LinksSection;
  hotProductSection: HotProductSection;
  prominentBannerSection?: ProminentBannerSection;
  basketToken?: BasketToken;
  error?: ProductBulkError;
  coinsBannerSection?: CoinsBannerSection;
}

// BAS Placement Section will only be placed on the Desktop stack
export interface BASPlacementSection {
  basPlacement?: Advertisement;
}

export interface ReportLegalConcernFormRequest {
  config: Config;
  productId: number;
  reason: ReportLegalConcernFormRequest_Reason;
  // min. 50, max. 500 characters
  description: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  isTrustedFlagger: boolean;
}

export interface ReportLegalConcernFormResponse {}

export interface PerfectPairRecoSection {
  // eg: Perfect Combination
  headline: string;
  productId: number;
  productName: string;
  brandName: string;
  productImage: ProductImage;
  // use [price_v2] instead
  price: Price;
  productTracker: ProductTracker;
  sectionTracker: SectionTracker;
  brandTracker: BrandTracker;
  priceV2: PriceV2;
}

// Request for GetProductStreamRecommendations (product stream variant 2).
export interface GetCombinedRecommendationsRequest {
  config: Config;
  session?: Session;
  productId: number;
  lastSeenProductIds: number[];
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
  customerName?: string;
}

// Response for GetProductStreamRecommendations.
export interface GetCombinedRecommendationsResponse {
  advertisement?: Advertisement;
  outfitSlider?: OutfitSlider;
  recommendationSection: GetCombinedRecommendationsResponse_RecommendationSection;
  lastSeenSection: GetCombinedRecommendationsResponse_LastSeenSection;
}

// Recommendation section: mixed recos (brand, similar, story, complementary, sponsored) plus AI dialogue.
export interface GetCombinedRecommendationsResponse_RecommendationSection {
  products: ProductTileV2[];
  aiDialogue: AIDialogue;
  promotionTracker?: PromotionTracker;
}

// Last seen section: only product tiles.
export interface GetCombinedRecommendationsResponse_LastSeenSection {
  products: ProductTileV2[];
  promotionTracker?: PromotionTracker;
}

// AI dialogue section shown at the bottom of the product stream.
export interface AIDialogue {
  headline: string;
  options: AIDialogueOption[];
}

export interface AIDialogueOption {
  // e.g. "More from the brand"
  label: string;
  recoType: RecoType;
  // Pre-computed: "Here are more items from Nike."
  aiResponseText: string;
}

// Request for GetSpecificRecommendation (when user taps an AI dialogue option).
export interface GetSpecificRecommendationRequest {
  config: Config;
  session?: Session;
  productId: number;
  targetGroup: TargetGroup;
  reductionsState: ReductionsState;
  recoType: RecoType;
  // Products already shown in super-stream to avoid duplicates.
  excludedProductIds: number[];
  // Types of recos that are available to be shown.
  availableRecoTypes: RecoType[];
  customerName?: string;
}

// Response for GetAIDialogueRecommendation.
export interface GetSpecificRecommendationResponse {
  // Full product list for the selected reco; even count enforced.
  products: ProductTileV2[];
  // AI dialogue section shown at the bottom of the product stream.
  aiDialogue: AIDialogue;
  promotionTracker?: PromotionTracker;
  // When reco_type is RECO_TYPE_BRAND:
  brandContent?: ProductRecommendation_BrandContent;
  // When reco type is teaser
  teaserContent?: ProductRecommendation_TeaserContent;
}
